// Confirm/reject UI for a `proposal_pending` event. Phase 10a.
//
// Renders the typed proposal payload and exposes `onConfirm` / `onReject`
// callbacks. The parent (chat surface) wires the callbacks to the WS layer;
// the server's apply seam runs the write on confirm. Decided proposals
// render their outcome inline; no double-confirms.

import type { Proposal } from "@loom/shared";
import { uiProse } from "@loom/shared/brand";

import { useFieldCopy } from "../../focus/copy";
import { useT } from "../../i18n/index.js";

interface ProposalCardProps {
  readonly proposalId: string;
  readonly proposal: Proposal;
  readonly state: "pending" | "confirmed" | "rejected";
  readonly onConfirm: (proposalId: string) => void;
  readonly onReject: (proposalId: string) => void;
}

export function ProposalCard({
  onConfirm,
  onReject,
  proposal,
  proposalId,
  state,
}: ProposalCardProps) {
  const t = useT();
  return (
    <div className="rounded-md border border-info-line-strong bg-info-soft p-3 text-xs text-info-fg-3 space-y-2">
      <ProposalSummary proposal={proposal} />
      {state === "pending" && (
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded bg-info-solid px-2 py-1 text-on-solid hover:bg-info-solid-hover"
            onClick={() => onConfirm(proposalId)}
          >
            {t("common.confirm")}
          </button>
          <button
            type="button"
            className="rounded border border-line-strong px-2 py-1 hover:bg-surface-3"
            onClick={() => onReject(proposalId)}
          >
            {t("common.reject")}
          </button>
        </div>
      )}
      {state === "confirmed" && (
        <div className="text-success-fg">{t("chatRender.applied")}</div>
      )}
      {state === "rejected" && (
        <div className="text-ink-subtle">{t("chatRender.rejected")}</div>
      )}
    </div>
  );
}

function ProposalSummary({ proposal }: { readonly proposal: Proposal }) {
  const t = useT();
  const fieldCopy = useFieldCopy();
  switch (proposal.kind) {
    case "enrich_legacy_patch":
      return (
        <div className="space-y-1">
          <div className="font-medium">
            {t("chatRender.moderniseLegacy", { iid: proposal.issueIid })}
          </div>
          {proposal.patch.title !== null && (
            <div>
              <span className="text-ink-subtle">{t("chatRender.titleField")}</span>{" "}
              <span>{proposal.patch.title}</span>
            </div>
          )}
          {proposal.patch.description !== null && (
            <div>
              <span className="text-ink-subtle">{t("chatRender.descriptionField")}</span>{" "}
              {t("chatRender.descriptionChars", {
                count: proposal.patch.description.length,
              })}
            </div>
          )}
          {proposal.patch.add_labels.length > 0 && (
            <div>
              <span className="text-ink-subtle">{t("chatRender.addLabels")}</span>{" "}
              <span className="font-mono">
                {proposal.patch.add_labels.join(", ")}
              </span>
            </div>
          )}
          {proposal.patch.remove_labels.length > 0 && (
            <div>
              <span className="text-ink-subtle">{t("chatRender.removeLabels")}</span>{" "}
              <span className="font-mono">
                {proposal.patch.remove_labels.join(", ")}
              </span>
            </div>
          )}
          {proposal.missingDorFields.length > 0 && (
            <div>
              <span className="text-ink-subtle">{t("chatRender.missingDorInline")}</span>{" "}
              {/* Labels, never the raw field ids — same map as every other
                  readiness surface. */}
              {proposal.missingDorFields
                .map((f) => fieldCopy(f).label)
                .join("; ")}
            </div>
          )}
          {proposal.patch.rationale && (
            <div className="italic text-ink-3">
              {uiProse(proposal.patch.rationale)}
            </div>
          )}
        </div>
      );
    case "dor_block_with_hints":
    case "dod_block_with_hints":
      return (
        <div className="space-y-1">
          <div className="font-medium">
            {proposal.kind === "dor_block_with_hints"
              ? t("chatRender.dorHints", { iid: proposal.issueIid })
              : t("chatRender.dodHints", { iid: proposal.issueIid })}
          </div>
          {proposal.missingFields.length > 0 && (
            <div>
              <span className="text-ink-subtle">{t("chatRender.missingInline")}</span>{" "}
              {proposal.missingFields.join("; ")}
            </div>
          )}
        </div>
      );
    case "create_issue_with_link_proposals":
      return (
        <div className="space-y-1">
          <div className="font-medium">
            {t("chatRender.createIssueProposal", { title: proposal.draft.title })}
          </div>
          {proposal.draft.labels.length > 0 && (
            <div>
              <span className="text-ink-subtle">{t("chatRender.labelsField")}</span>{" "}
              <span className="font-mono">
                {proposal.draft.labels.join(", ")}
              </span>
            </div>
          )}
          {proposal.candidates.length > 0 && (
            <div>
              <span className="text-ink-subtle">{t("chatRender.linksField")}</span>{" "}
              {proposal.candidates.length} candidate
              {proposal.candidates.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
      );
  }
}
